export const formatMarketCap = (marketCap: number): string => {
  if (!marketCap || isNaN(marketCap)) return '--';

  const absValue = Math.abs(marketCap);
  const sign = marketCap < 0 ? '-' : '';

  if (absValue >= 1e12) {
    return `${sign}$${(absValue / 1e12).toFixed(2)} T`;
  } else if (absValue >= 1e9) {
    return `${sign}$${(absValue / 1e9).toFixed(2)} B`;
  } else if (absValue >= 1e6) {
    return `${sign}$${(absValue / 1e6).toFixed(2)} M`;
  } else if (absValue >= 1e3) {
    return `${sign}$${(absValue / 1e3).toFixed(2)} K`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(marketCap);
};

export const formatRevenue = (value: number | null | undefined): string => {
  if (value == null) return '--';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
};

export const getTextColorClass = (value: number, decimalPlaces = 2) => {
  const rounded = Number(value.toFixed(decimalPlaces));

  if (rounded === 0 || Object.is(rounded, -0)) return 'text-white';

  return rounded > 0 ? 'text-brand-green-primary' : 'text-red-500';
};

export const getTextColorClassPercent = (value: number) => {
  if (Math.abs(value) < 0.05) {
    return 'text-white';
  }

  return value > 0 ? 'text-brand-green-primary' : 'text-red-500';
};

export const formatEPS = (value: number | null | undefined): string => {
  if (value == null) return '--';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatNumber = (value: number | null | undefined, decimals: number = 1): string => {
  if (value == null) return '--';

  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: 'auto',
  }).format(value);
};

export const formatShares = (shares: number): string => {
  if (!shares || isNaN(shares)) return '--';

  if (shares >= 1e9) {
    return `${(shares / 1e9).toFixed(1)}B`;
  } else if (shares >= 1e6) {
    return `${(shares / 1e6).toFixed(1)}M`;
  } else if (shares >= 1e3) {
    return `${(shares / 1e3).toFixed(1)}K`;
  }
  return shares.toLocaleString();
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: 'auto',
  }).format(value / 100);
};

export const calculateSurprisePercentage = (
  actual: number | null,
  estimated: number | null,
): number | null => {
  if (estimated == null || estimated === 0 || actual == null) return null;

  const surprise = actual - estimated;

  if (Math.abs(surprise) < 0.005) return 0;

  const result = (surprise / Math.abs(estimated)) * 100;

  if (!isFinite(result) || isNaN(result)) return null;

  return result;
};

export const calculateChangePercentage = (
  actual: number | null,
  previous: number | null,
): number | null => {
  if (previous == null || previous === 0 || actual == null) return null;

  const change = actual - previous;

  if (Math.abs(change) < 0.005) return 0;

  const result = (change / Math.abs(previous)) * 100;

  if (!isFinite(result) || isNaN(result)) return null;

  return result;
};

/**
 * Formats a calendar day (`YYYY-MM-DD`). `new Date('2026-11-19')` is UTC midnight, so formatting
 * it in the viewer's zone shows the previous day anywhere west of UTC. Format it in UTC instead.
 */
export const formatCalendarDay = (date: string, options: Intl.DateTimeFormatOptions): string =>
  new Date(date).toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
